// Changing stock counts from any stock number (migration 065; Mitch, 2026-11-03: available, plus moving units to damaged,
// quality control, safety stock or reserved, by clicking any stock number, admin and inventory). A Shopify item's counts
// at an HHL location are typed as they are: what's held back moves to or from available first, then available is set
// (on hand follows), each with the count seen, so Shopify refuses if it changed meanwhile (stock.change_shopify, sent by
// the Shopify worker). A local item is counted in HHL Ops' own ledger (stock.adjust). Committed is customer orders'.
import { RotateCcw } from "lucide-react";
import { type ReactNode, useState } from "react";
import { numberOf } from "../../../shared/calc.ts";
import type { Item, Location, StockChange } from "../../../shared/db.gen.ts";
import { STOCK_STATE_LABELS } from "../../../shared/mutations-receiving.ts";
import { qty } from "../lib/format.ts";
import { can, mutate, mutateWithResult } from "../lib/mutate.ts";
import { useRows } from "../lib/sync.ts";
import { Button, cx, Input, Select } from "./controls.tsx";
import { Popover } from "./Popover.tsx";
import { toast } from "./toast.tsx";

type Held = "damaged" | "quality_control" | "safety_stock" | "reserved";
const HELD: Held[] = ["damaged", "quality_control", "safety_stock", "reserved"];

const hhlPlaces = (locations: readonly Location[], shopify: boolean) =>
  locations.filter((l) => l.kind === "hhl" && l.active && !l.ignored && (!shopify || !!l.shopify_location_id));

/**
 * A stock number that opens the editor where it's clicked (for people who can change stock); otherwise just the number.
 * Clicking it never opens the row it's in.
 */
export function StockNumber({
  itemId,
  children,
  className,
  title,
}: {
  itemId: string | null | undefined;
  children: ReactNode;
  className?: string;
  title?: string;
}) {
  if (!itemId || !(can("stock.change_shopify") || can("stock.adjust")))
    return (
      <span className={className} title={title}>
        {children}
      </span>
    );
  return (
    <Popover
      width={340}
      button={(toggle, open) => (
        <button
          type="button"
          title={title ? `${title} · Click to change` : "Click to change the counts"}
          onClick={(e) => {
            e.stopPropagation();
            toggle();
          }}
          className={cx("rounded px-0.5 hover:bg-accent-soft hover:text-accent-text", open && "bg-accent-soft", className)}
        >
          {children}
        </button>
      )}
    >
      {(close) => <StockEditor itemId={itemId} close={close} />}
    </Popover>
  );
}

function StockEditor({ itemId, close }: { itemId: string; close: () => void }) {
  const items = useRows("item");
  const item = items.find((i) => i.id === itemId);
  if (!item) return null;
  return item.source === "shopify" && item.shopify_inventory_item_id ? <ShopifyCounts item={item} close={close} /> : <LedgerCount item={item} close={close} />;
}

function ShopifyCounts({ item, close }: { item: Item; close: () => void }) {
  const locations = useRows("location");
  const levels = useRows("stock_level");
  const changes = useRows("stock_change");
  const places = hhlPlaces(locations, true);
  const levelAt = (id: string) => levels.find((l) => l.item_id === item.id && l.location_id === id);
  const [placeId, setPlaceId] = useState(
    () => [...places].sort((a, b) => Number(levelAt(b.id)?.on_hand ?? 0) - Number(levelAt(a.id)?.on_hand ?? 0))[0]?.id ?? "",
  );
  const level = levelAt(placeId);
  const now: Record<"available" | Held, number> = {
    available: Number(level?.available ?? 0),
    damaged: Number(level?.damaged ?? 0),
    quality_control: Number(level?.quality_control ?? 0),
    safety_stock: Number(level?.safety_stock ?? 0),
    reserved: Number(level?.reserved ?? 0),
  };
  const [typed, setTyped] = useState<Partial<Record<"available" | Held, string>>>({});
  const [busy, setBusy] = useState(false);
  const value = (k: "available" | Held) => (typed[k] === undefined ? now[k] : numberOf(typed[k]));
  const committed = Number(level?.committed ?? 0);
  const values = (["available", ...HELD] as const).map((k) => value(k));
  const bad = values.some((n) => !Number.isInteger(n) || n < 0);
  const onHand = bad ? null : values.reduce((a, b) => a + b, 0) + committed;
  const mine = changes.filter((c) => c.item_id === item.id && c.location_id === placeId && c.status !== "done");
  const editable = can("stock.change_shopify");

  // Held-back counts move to or from available first, then available is set, each with the count it was seen at.
  const plan = () => {
    const out: { id: string; kind: "set" | "move"; from_state?: string; to_state?: string; qty: number; seen: number | null }[] = [];
    let available = now.available;
    for (const h of HELD) {
      const delta = value(h) - now[h];
      if (delta > 0) out.push({ id: crypto.randomUUID(), kind: "move", from_state: "available", to_state: h, qty: delta, seen: available });
      if (delta < 0) out.push({ id: crypto.randomUUID(), kind: "move", from_state: h, to_state: "available", qty: -delta, seen: now[h] });
      available -= delta;
    }
    if (value("available") !== available) out.push({ id: crypto.randomUUID(), kind: "set", qty: value("available"), seen: available });
    return out;
  };
  const save = async () => {
    const list = plan();
    if (!list.length) return close();
    setBusy(true);
    const { ok } = await mutateWithResult("stock.change_shopify", { item_id: item.id, location_id: placeId, changes: list as never });
    setBusy(false);
    if (!ok) return;
    toast.ok(`${item.sku || item.name}: sending ${list.length === 1 ? "the change" : `${list.length} changes`} to Shopify`);
    close();
  };

  return (
    <form
      className="flex flex-col gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        if (!bad && editable) void save();
      }}
    >
      <div className="font-medium">{item.sku || item.name} in Shopify</div>
      {places.length > 1 && (
        <Select
          aria-label="Location"
          value={placeId}
          onChange={(e) => setPlaceId(e.target.value)}
          options={places.map((p) => ({ value: p.id, label: p.name }))}
        />
      )}
      <div className="grid grid-cols-[1fr_96px] items-center gap-x-3 gap-y-1.5 text-[12px]">
        {(["available", ...HELD] as const).map((k) => (
          <Row key={k} label={STOCK_STATE_LABELS[k]} hint={k === "available" ? "To sell" : "On hand, not for sale"}>
            <Input
              aria-label={STOCK_STATE_LABELS[k]}
              value={typed[k] ?? String(now[k])}
              disabled={!editable}
              onChange={(e) => setTyped({ ...typed, [k]: e.target.value })}
              className={cx("h-7 text-right font-mono text-[12px]", typed[k] !== undefined && value(k) !== now[k] && "border-accent")}
            />
          </Row>
        ))}
        <Row label="Committed" hint="On customer orders (not changed here)">
          <span className="pr-2 text-right font-mono text-muted">{qty(committed)}</span>
        </Row>
        <Row label="On hand" hint="All of these">
          <span className="pr-2 text-right font-mono font-medium">{onHand === null ? "—" : qty(onHand)}</span>
        </Row>
      </div>
      {bad && <div className="text-[12px] text-warn">Counts are whole numbers, 0 or more.</div>}
      {mine.map((c) => (
        <ChangeLine key={c.id} c={c} />
      ))}
      <div className="flex items-center justify-end gap-2">
        <span className="mr-auto text-[11px] text-faint">Sent to Shopify; refused if its count changed since</span>
        <Button size="sm" variant="ghost" onClick={close}>
          Cancel
        </Button>
        <Button size="sm" variant="primary" type="submit" disabled={bad || busy || !editable || !placeId}>
          Save
        </Button>
      </div>
    </form>
  );
}

function Row({ label, hint, children }: { label: string; hint: string; children: ReactNode }) {
  return (
    <>
      <span>
        {label} <span className="text-faint">· {hint}</span>
      </span>
      {children}
    </>
  );
}

/** A change still on its way, or one Shopify refused (with Retry). */
function ChangeLine({ c }: { c: StockChange }) {
  const what =
    c.kind === "set"
      ? `Available to ${qty(c.qty)}`
      : `${qty(c.qty)} from ${STOCK_STATE_LABELS[c.from_state as Held] ?? c.from_state} to ${STOCK_STATE_LABELS[c.to_state as Held] ?? c.to_state}`;
  return (
    <div
      className={cx(
        "flex items-start gap-2 rounded-md px-2 py-1 text-[12px]",
        c.status === "failed" ? "bg-danger-soft text-danger" : "bg-surface-2 text-muted",
      )}
    >
      <span className="min-w-0 flex-1">
        {what}: {c.status === "failed" ? c.error || "Shopify refused it" : "sending to Shopify…"}
      </span>
      {c.status === "failed" && can("stock_change.retry") && (
        <button
          type="button"
          className="flex shrink-0 items-center gap-1 font-medium hover:underline"
          onClick={() => void mutate("stock_change.retry", { id: c.id })}
        >
          <RotateCcw className="size-3" /> Retry
        </button>
      )}
    </div>
  );
}

/** A local item (materials, sub-assemblies): its count at an HHL location in HHL Ops' own ledger. */
function LedgerCount({ item, close }: { item: Item; close: () => void }) {
  const locations = useRows("location");
  const balances = useRows("stock_balance");
  const places = hhlPlaces(locations, false);
  const at = (id: string) => Number(balances.find((b) => b.item_id === item.id && b.location_id === id)?.qty ?? 0);
  const [placeId, setPlaceId] = useState(() => [...places].sort((a, b) => at(b.id) - at(a.id))[0]?.id ?? "");
  const [text, setText] = useState(String(at(placeId)));
  const n = numberOf(text);
  const ok = Number.isFinite(n) && n >= 0;
  const save = async () => {
    if (!ok || n === at(placeId)) return close();
    if (
      await mutate("stock.adjust", { id: crypto.randomUUID(), item_id: item.id, location_id: placeId, kind: "count", qty: n, notes: "Counted (stock number)" })
    )
      close();
  };
  return (
    <form
      className="flex flex-col gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        void save();
      }}
    >
      <div className="font-medium">Count {item.sku || item.name}</div>
      <div className="text-[12px] text-muted">HHL Ops keeps its stock (it isn't in Shopify): the count is a move on its ledger.</div>
      {places.length > 1 && (
        <Select
          aria-label="Location"
          value={placeId}
          onChange={(e) => {
            setPlaceId(e.target.value);
            setText(String(at(e.target.value)));
          }}
          options={places.map((p) => ({ value: p.id, label: p.name }))}
        />
      )}
      <label className="flex items-center justify-between gap-3 text-[12px]">
        On hand
        <Input aria-label="On hand" value={text} onChange={(e) => setText(e.target.value)} className="h-7 w-24 text-right font-mono text-[12px]" />
      </label>
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={close}>
          Cancel
        </Button>
        <Button size="sm" variant="primary" type="submit" disabled={!ok || !can("stock.adjust")}>
          Save
        </Button>
      </div>
    </form>
  );
}
